import './perks-post-summary.component.scss';

import dayjs from 'dayjs';
import { h } from 'preact';
import { useMemo } from 'preact/hooks';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { useAuth } from '../../../auth/hooks/use-auth.hook';
import { IconText } from '../../../common/icons/icon-text';
import { useTranslation } from '../../../i18n';
import type { NeedAccessReq } from '../../../need-access/need-access.interface';
import { NeedAccessContentType } from '../../../need-access/need-access.interface';
import { NeedAccessService } from '../../../need-access/need-access.service';
import { useAppDispatch } from '../../../store/hooks';
import { linkify } from '../../../utils/a11y.utils';
import type { Post } from '../../model/post.interface';
import { PerksHelper } from '../../perks.helper';
import { hiddenPostImage } from '../perks-posts/perks-post/perks-post-media/hidden-image-base-64';
import { PerksPostMediaContent } from '../perks-posts/perks-post/perks-post-media/perks-post-media-content.component';

export const PerksPostSummary = ({ key, post }: { key: string; post: Post }) => {
  const needAccessService = new NeedAccessService();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { openAuthModal } = useAuth();
  const [location, setLocation] = useHashLocation();
  const shouldShowContent =
    !post.hidden || (post.hidden && post.contents && post.contents.length === 0);

  const title = useMemo((): string => PerksHelper.getTitle(post), [post]);

  const handleNeedAccess = async () => {
    if (!post?.id) return;

    const req: NeedAccessReq = {
      contentId: post.id,
      contentType: NeedAccessContentType.POST,
    };
    // dispatch + openAuthModal let the service route a single-free-tier store
    // straight into the join flow, same as the Join to Unlock / Join to
    // comment buttons — without them every click lands on the modal
    await needAccessService.processNeedAccessRequest({
      request: req,
      dispatch,
      openAuthModal,
    });
  };

  const goToPost = async () => {
    // any post the visitor can't access prompts the join flow — not just
    // hidden stubs; a public-preview post is not hidden but is still locked
    // (=== false so a list payload omitting the field keeps navigating)
    if (!!post?.hidden || post?.userHasAccess === false) {
      await handleNeedAccess();
    } else {
      setLocation(`/posts/${post.id}`);
    }
  };

  return (
    <div
      class={`single-post single-post-summary ${post.hidden ? 'post-hidden' : ''}`}
      {...linkify(goToPost, post.title)}
    >
      {shouldShowContent ? (
        <>
          {post.contents && post.contents.length > 0 ? (
            <PerksPostMediaContent
              post={post}
              showSlider={false}
              showPlayer={false}
              showNeedAccessPlayBtn={false}
            />
          ) : (
            <div class="single-text-post-media">
              <IconText />
            </div>
          )}
        </>
      ) : (
        <div class="single-post-media post-hidden">
          <div class="image-container">
            <img src={hiddenPostImage} alt={t('perks.hiddenPostImageAlt')} />
          </div>
        </div>
      )}

      <div class={`single-post-body ${post.hidden ? 'post-hidden' : ''}`}>
        <h5 title={title}>{title}</h5>
        <p>{dayjs(post.publishedAt).format('MMM DD, YYYY')}</p>
      </div>
    </div>
  );
};
